import { track } from './analytics/track.js';
import { EVENT_CATEGORIES } from './analytics/constants/event-categories.js';
import { initializeBootBudget } from './routes/root/boot-budget.js';
import { ensureNotNil } from '@studio/js-utils';
// import { ReactScanController } from './react-scan/react-scan-controller.js'; TODO: Add this back in
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Root } from './app/components/root/index.js';
// Spec 4 (D1): open the per-document startup partition. Imported from a module
// with NO app dependencies so instrumentation cannot perturb the eager graph it
// measures. This call is AFTER the static imports above — it observes their
// completion, not their cost; the pre-entry bucket carries that interval and the
// external trace (not this recorder) splits it.
import { startRendererDocument } from './boot/renderer-startup.js';

// Per think-in-systems (ai.knowledge/global): import the E2E renderer probes
// once at boot. Each module is a side-effect: it self-installs its
// `globalThis.__iris*Probe` iff IRIS_E2E_TEST=1, otherwise it's a no-op
// (and dead-code-eliminated by Vite when the gate evaluates to a constant).
import './test-probes/device-probe.js';
import './test-probes/preset-group-probe.js';
import './test-probes/native-preset-submission-probe.js';
import './test-probes/source-codec-probe.js';
import './test-probes/calibration-probe.js';
import './test-probes/billing-probe.js';
import './test-probes/config-badge-probe.js';
import './test-probes/bandwidth-governor-probe.js';
import './test-probes/permissions-probe.js';
import './test-probes/encoder-error-probe.js';
import './test-probes/discovery-mismatch-probe.js';
import './test-probes/download-origin-probe.js';
import './test-probes/web-feature-gate-probe.js';
import './test-probes/preset-action-telemetry-probe.js';
// The ONLY probe gated on a BROWSER harness rather than the Electron preload
// bridge: the standalone-web funnel it drives cannot be observed from an Electron
// renderer at all, because `client` is resolved from the presence of that preload.
// See its header for the three-condition gate that keeps it unreachable in a
// deployed web build.
import './test-probes/web-funnel-probe.js';
import './test-probes/terminal-outcome-probe.js';
import './test-probes/renderer-startup-probe.js';
import './test-probes/device-grain-telemetry-probe.js';
import './test-probes/preset-group-idempotency-probe.js';
import './test-probes/create-studio-link-probe.js';
import './test-probes/onboarding-telemetry-probe.js';
import './test-probes/registration-telemetry-probe.js';
import './test-probes/verification-gate-telemetry-probe.js';
import './test-probes/studio-link-failed-telemetry-probe.js';
import './test-probes/onboarding-auto-link-probe.js';
import './test-probes/onboarding-magic-moment-probe.js';
import './test-probes/permissions-telemetry-probe.js';
import './test-probes/media-controls-telemetry-probe.js';

import { Vendor, getCurrentVendor } from '@studio/design-system/themes/vendor.js';

const TitleMap: Record<Vendor, string> = {
  ptzoptics: 'Hive',
  iris: 'Iris Studio'
};

const Title = TitleMap[getCurrentVendor()] ?? TitleMap.iris;

window.document.title = Title!;

function changeFavicon(src: string): void {
  document.querySelectorAll("link[rel*='icon']").forEach(
    // @ts-expect-error
    (e) => (e.href = src)
  );
}

const faviconMap: Record<Vendor, string> = {
  ptzoptics: '/assets/ptzoptics/hive_icon.png',
  iris: '/assets/favicon.ico'
};

changeFavicon(faviconMap[getCurrentVendor()] ?? faviconMap.iris);

// Spec 4 (D1) opens the per-document startup partition; ENG-4720 arms the boot
// budget. Independent and both wanted: the recorder measures what the budget
// bounds. The recorder goes first so the budget's own arming is inside the
// measured window rather than before it.
startRendererDocument();

initializeBootBudget((name, properties) => {
  void track({
    name,
    category: EVENT_CATEGORIES.APP_LIFECYCLE,
    timestamp: Date.now(),
    properties
  }).catch(() => {});
});

createRoot(ensureNotNil(document.querySelector('#root'))).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
